import * as THREE from 'three';
import { Registry, EventBus } from './registry.js';
import { FIXED_DT, MAX_SUBSTEPS } from './config.js';
import { Input } from './input.js';
import { Rng } from './rng.js';

/**
 * The Engine owns the frame loop and the shared context handed to every
 * subsystem. It does NOT know what any subsystem does — it only sequences them.
 *
 * Frame order:
 *   1. input.beginFrame()
 *   2. fixedUpdate(FIXED_DT) xN   — physics, deterministic gameplay
 *   3. update(dt)                 — animation, cameras, AI decisions
 *   4. lateUpdate(dt)             — anything that must observe final transforms
 *   5. render subsystem draws
 *   6. input.endFrame()
 */
export class Engine {
  constructor({ canvas, config }) {
    this.canvas = canvas;
    this.config = config;
    this.registry = new Registry();
    this.events = new EventBus();
    this.input = new Input(canvas, config);
    this.rng = new Rng(config.deterministic ? 0x5eed1234 : (Math.random() * 2 ** 32) >>> 0);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(config.fov, 1, 0.05, 1200);
    this.camera.rotation.order = 'YXZ';

    /** Separate scene+camera for the first-person viewmodel, drawn with its own
     *  near plane so hands/weapon never clip into world geometry. */
    this.viewScene = new THREE.Scene();
    this.viewCamera = new THREE.PerspectiveCamera(60, 1, 0.005, 12);

    this.time = {
      /** Seconds since start, scaled. */ elapsed: 0,
      /** Unscaled wall-clock seconds since start. */ raw: 0,
      /** Last frame delta, scaled and clamped. */ dt: 0,
      /** Fixed step. */ fixed: FIXED_DT,
      /** Interpolation alpha between the last two physics steps, 0..1. */ alpha: 0,
      scale: 1,
      frame: 0,
    };

    this.ctx = {
      engine: this,
      scene: this.scene,
      camera: this.camera,
      viewScene: this.viewScene,
      viewCamera: this.viewCamera,
      canvas,
      config,
      events: this.events,
      input: this.input,
      time: this.time,
      rng: this.rng,
      get: (id) => this.registry.get(id),
      peek: (id) => this.registry.peek(id),
      has: (id) => this.registry.has(id),
    };

    this._accum = 0;
    this._last = 0;
    this._running = false;
    this._onResize = () => this.resize();

    /**
     * Minimum seconds between rendered frames, or 0 for "as fast as the display
     * will take". This renderer will consume an entire GPU if you let it — the
     * ultra preset is four 4096 shadow cascades, GTAO, SSR, TAA and volumetrics
     * — so on a 144 Hz panel an uncapped loop is a fan event. `?fps=N` caps it.
     */
    // Default 60 rather than uncapped. A shooter does not need more, and on a
    // 144 Hz panel the difference is the whole GPU. `?fps=0` removes the cap.
    this._minFrameTime = 1 / 60;
    const q = new URLSearchParams(location.search);
    // `has` first: Number(null) is 0, which is finite, so reading the param
    // unconditionally turned "no ?fps given" into "?fps=0" and silently
    // uncapped the loop — the default below never once applied.
    if (q.has('fps')) {
      const fps = Number(q.get('fps'));
      if (Number.isFinite(fps)) this._minFrameTime = fps > 0 ? 1 / fps : 0;
    }
    this._frameDebt = 0;

    /**
     * A backgrounded tab must not keep rendering. Chrome throttles rAF when a
     * tab is hidden but does NOT when it is merely unfocused behind another
     * window, so a game left open in a visible-but-inactive tab keeps the GPU
     * pinned indefinitely. Suspend on hide, and resume without a dt spike.
     */
    this._suspended = false;
    this._onVisibility = () => {
      const hidden = document.visibilityState === 'hidden';
      if (hidden === this._suspended) return;
      this._suspended = hidden;
      // Coming back: re-base the clock, or the accumulated wall time would be
      // handed to the physics loop as one enormous step.
      if (!hidden) this._last = performance.now();
    };
  }

  /** Skip the render but keep the page alive. Used by the visibility guard. */
  get suspended() {
    return this._suspended;
  }

  /** Frames per second ceiling; 0 = uncapped. Live-adjustable. */
  get frameCap() {
    return this._minFrameTime > 0 ? Math.round(1 / this._minFrameTime) : 0;
  }
  set frameCap(fps) {
    this._minFrameTime = fps > 0 ? 1 / fps : 0;
    this._frameDebt = 0;
  }

  add(SystemClass, opts) {
    this.registry.add(new SystemClass(opts));
    return this;
  }

  async init() {
    const order = this.registry.resolve();
    for (const sys of order) {
      const t0 = performance.now();
      await sys.init?.(this.ctx);
      const ms = performance.now() - t0;
      if (ms > 50) console.info(`[engine] ${sys.constructor.id} init ${ms.toFixed(0)}ms`);
    }
    this.input.attach();
    addEventListener('resize', this._onResize);
    document.addEventListener('visibilitychange', this._onVisibility);
    this.resize();
    return this;
  }

  resize() {
    const w = Math.max(1, this.canvas.clientWidth || innerWidth);
    const h = Math.max(1, this.canvas.clientHeight || innerHeight);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.viewCamera.aspect = w / h;
    this.viewCamera.updateProjectionMatrix();
    for (const sys of this.registry.with('resize')) sys.resize(w, h, this.ctx);
    this.events.emit('resize', { width: w, height: h });
  }

  start() {
    if (this._running) return;
    this._running = true;
    this._last = performance.now();
    this._loop = this._loop.bind(this);
    requestAnimationFrame(this._loop);
  }

  stop() {
    this._running = false;
  }

  _loop(now) {
    if (!this._running) return;
    requestAnimationFrame(this._loop);
    this.step(now);
  }

  /** Advance one frame. Exposed so the capture harness can pump frames by hand. */
  step(now = performance.now()) {
    const t = this.time;
    // Clamp so a tab-switch or a breakpoint doesn't teleport the simulation.
    const rawDt = Math.min(0.1, Math.max(0, (now - this._last) / 1000));

    // Hidden tab: hold the clock still and draw nothing. The simulation is not
    // advanced either — a shooter that keeps running while you cannot see it is
    // a worse outcome than one that pauses.
    if (this._suspended) {
      this._last = now;
      return;
    }

    // Frame cap. `_last` is deliberately NOT advanced on a skipped frame: the
    // next one that runs must be handed the FULL elapsed wall time, not just
    // the slice since the last skip. Advancing it here was a bug — on a 240 Hz
    // display capped to 60 it fed the simulation a quarter of the real dt every
    // frame, so the whole game ran in slow motion while the clock did not.
    if (this._minFrameTime > 0 && rawDt < this._minFrameTime) return;

    this._last = now;
    t.raw += rawDt;
    t.dt = rawDt * t.scale;
    t.elapsed += t.dt;
    t.frame++;

    this.input.beginFrame();

    this._accum += t.dt;
    let steps = 0;
    const fixedSystems = this.registry.with('fixedUpdate');
    while (this._accum >= FIXED_DT && steps < MAX_SUBSTEPS) {
      for (const sys of fixedSystems) sys.fixedUpdate(FIXED_DT, this.ctx);
      this._accum -= FIXED_DT;
      steps++;
    }
    if (steps === MAX_SUBSTEPS) this._accum = 0; // shed backlog rather than spiral
    t.alpha = this._accum / FIXED_DT;

    for (const sys of this.registry.with('update')) sys.update(t.dt, this.ctx);
    for (const sys of this.registry.with('lateUpdate')) sys.lateUpdate(t.dt, this.ctx);

    const renderSystem = this.registry.peek('render');
    if (typeof renderSystem?.render === 'function') renderSystem.render(this.ctx);

    this.input.endFrame();
  }

  dispose() {
    this.stop();
    removeEventListener('resize', this._onResize);
    this.input.detach();
    for (const sys of [...this.registry.ordered].reverse()) sys.dispose?.();
    this.events.clear();
  }
}
